import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, AlertTriangle, FileJson, CheckCircle2 } from "lucide-react";

const COLLECTION_LABELS = {
  cars: "Mașini",
  clients: "Clienți",
  expenses: "Cheltuieli mașini",
  fixed_costs: "Costuri fixe",
  recurring_templates: "Costuri recurente",
  sales: "Vânzări",
  interactions: "Interacțiuni",
  calendar_events: "Calendar (evenimente)",
  files: "Fișiere",
};

export default function ImportDataDialog({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("merge"); // "merge" | "replace"
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const fileInputRef = useRef(null);

  function reset() {
    setFile(null);
    setMode("merge");
    setResult(null);
    setConfirmReplace(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function handleClose() {
    if (loading) return;
    reset();
    onClose?.();
  }

  async function submit() {
    if (!file) {
      toast.error("Alege un fișier JSON de import");
      return;
    }
    if (mode === "replace" && !confirmReplace) {
      toast.error("Confirmă opțiunea \"Înlocuire totală\" - operațiunea șterge datele existente");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/import.json?mode=${mode}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(
        `Import reușit · ${data.total_inserted} adăugate · ${data.total_updated} actualizate`
      );
      onImported?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Eroare la import");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-lg" data-testid="import-data-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <Upload className="w-5 h-5" /> Import date (restore backup)
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Încarcă un fișier <code className="font-mono text-xs bg-slate-100 px-1 py-0.5 rounded">novoauto-backup.json</code> exportat anterior pentru a restaura datele.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3">
            <div className="flex items-start gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold text-emerald-900">Import finalizat</div>
                <div className="text-emerald-700 text-xs mt-0.5">
                  Mod: <strong>{result.mode === "merge" ? "Îmbinare (merge)" : "Înlocuire totală"}</strong>
                  {" · "}{result.total_inserted} adăugate · {result.total_updated} actualizate
                </div>
              </div>
            </div>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                    <th className="px-3 py-2 font-medium">Colecție</th>
                    <th className="px-3 py-2 text-right font-medium">Adăugate</th>
                    <th className="px-3 py-2 text-right font-medium">Actualizate</th>
                    <th className="px-3 py-2 text-right font-medium">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {Object.entries(result.by_collection || {}).map(([k, v]) => (
                    <tr key={k}>
                      <td className="px-3 py-2">{COLLECTION_LABELS[k] || k}</td>
                      <td className="px-3 py-2 text-right tabular text-emerald-600">{v.inserted ?? 0}</td>
                      <td className="px-3 py-2 text-right tabular text-slate-700">{v.updated ?? 0}</td>
                      <td className="px-3 py-2 text-right tabular text-slate-500">{v.total ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Fișier backup</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/json,.json"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-1 w-full text-sm file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                data-testid="import-file-input"
              />
              {file && (
                <div className="flex items-center gap-2 text-xs text-slate-500 mt-1.5">
                  <FileJson className="w-4 h-4" />
                  <span className="truncate">{file.name}</span>
                  <span className="text-slate-400">· {(file.size / 1024).toFixed(1)} KB</span>
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Mod import</Label>
              <div className="mt-2 space-y-2">
                <label className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 has-[:checked]:border-blue-300 has-[:checked]:bg-blue-50/50 transition">
                  <input
                    type="radio"
                    name="mode"
                    value="merge"
                    checked={mode === "merge"}
                    onChange={() => { setMode("merge"); setConfirmReplace(false); }}
                    className="mt-0.5"
                    data-testid="import-mode-merge"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-sm">Îmbinare (recomandat)</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Păstrează datele existente. Înregistrările din backup sunt adăugate sau actualizate după ID.
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 has-[:checked]:border-rose-300 has-[:checked]:bg-rose-50/50 transition">
                  <input
                    type="radio"
                    name="mode"
                    value="replace"
                    checked={mode === "replace"}
                    onChange={() => setMode("replace")}
                    className="mt-0.5"
                    data-testid="import-mode-replace"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-sm text-rose-700 flex items-center gap-1.5">
                      <AlertTriangle className="w-4 h-4" /> Înlocuire totală (periculos)
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Șterge toate datele actuale și pune doar ce e în backup. Folosește doar pentru restore complet pe DB goală.
                    </div>
                  </div>
                </label>
              </div>
              {mode === "replace" && (
                <label className="mt-3 flex items-start gap-2 p-2.5 bg-rose-50 border border-rose-200 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={confirmReplace}
                    onChange={(e) => setConfirmReplace(e.target.checked)}
                    className="mt-0.5"
                    data-testid="import-confirm-replace"
                  />
                  <span className="text-xs text-rose-800">
                    Înțeleg că această operațiune <strong>șterge ireversibil</strong> toate datele actuale înainte de import.
                  </span>
                </label>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button onClick={handleClose} data-testid="import-close-btn">Închide</Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose} disabled={loading}>Anulează</Button>
              <Button onClick={submit} disabled={loading || !file} data-testid="import-submit-btn" className="bg-blue-600 hover:bg-blue-700">
                {loading ? "Se importă..." : <><Upload className="w-4 h-4 mr-1.5" />Importă datele</>}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
